import {
    AuthenticateOptions, AuthenticateResponse,
    AuthenticationEncoded,
    AuthType,
    NamedAlgo,
    NumAlgo,
    RegisterOptions,
    RegistrationEncoded
} from "./types.js";
import * as utils from './utils.js'
import {initRandomUUID} from "./index.js";
import axios from 'axios';


// const getDeviceInfo = async () => {
//     const fpPromise = FingerprintJS.load();
//     const result = await fpPromise
//         .then(fp => fp.get())
//         .then(result => {
//             // This is the visitor identifier:
//             const visitorId = result.visitorId;
//             console.log('Visitor ID:', visitorId);
//
//             // Access the detailed components of the fingerprint
//             const components : any = result.components;
//             console.log(components)
//             // Retrieve the desired details
//             const browserName = components.userAgentData.brands.map((brand: any) => brand.brand).join(', ');
//             const browserVersion = components.userAgentData.brands.map((brand: any) => brand.version).join(', ');
//             const osName = components.os.name;
//             const osVersion = components.os.version;
//             const deviceName = components.device.model;
//
//             // Log the details
//             console.log('Browser Name:', browserName);
//             console.log('Browser Version:', browserVersion);
//             console.log('OS Name:', osName);
//             console.log('OS Version:', osVersion);
//             console.log('Device Name:', deviceName);
//             return {
//                 browserName,
//                 browserVersion,
//                 osName,
//                 osVersion,
//                 deviceName
//             }
//         });
// }

function getDeviceInfo() {
    // if (!navigator) return null;
    // //设备的地理位置信息
    // const geolocation = navigator.geolocation;
    let deviceInfo = localStorage.getItem('deviceInfo');
    if(!deviceInfo) {
        deviceInfo = window.crypto.randomUUID();
        localStorage.setItem('deviceInfo', deviceInfo);
    }
    return deviceInfo;
}

/**
 * 根据算法编号获取算法名称。
 * @param {NumAlgo} num 算法编号。
 * @returns {NamedAlgo} 返回对应的算法名称。
 */
function getAlgoName(num: NumAlgo): NamedAlgo {
    // 根据算法编号返回相应的算法名称
    switch (num) {
        case -7:
            return "ES256"
        // case -8 ignored to its rarity
        case -257:
            return "RS256"
        default:
            throw new Error(`Unknown algorithm code: ${num}`)
    }
}

/**
 * 检查设备本身是否可以用作认证器。
 * @returns {Promise<boolean>} 如果设备可以作为认证器使用，则返回 true，否则返回 false。
 */
export async function isLocalAuthenticator(): Promise<boolean> {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
}


/**
 * 根据认证类型获取认证附件类型。
 * @param {AuthType} authType 认证类型。
 * @returns {Promise<AuthenticatorAttachment|undefined>} 根据认证类型返回相应的认证附件类型。
 */
async function getAuthAttachment(authType: AuthType): Promise<AuthenticatorAttachment | undefined> {
    // 根据 authType 分别返回 platform 或 cross-platform，如果类型是 both，则返回 undefined
    if (authType === "local")
        return "platform";
    if (authType === "roaming" || authType === "extern")
        return "cross-platform";
    if (authType === "both")
        // The webauthn protocol considers `null` as invalid but `undefined` as "both"!
        return undefined;

    // 默认情况下，根据设备能力选择使用 auto 模式
    try {
        if (await isLocalAuthenticator())
            return "platform"
        else
            return "cross-platform"
    } catch (e) {
        // 可能由于一些安全策略导致的错误
        // see https://w3c.github.io/webauthn/#sctn-isUserVerifyingPlatformAuthenticatorAvailable
        return undefined // The webauthn protocol considers `null` as invalid but `undefined` as "both"!
    }
}


export function checkIdRegistered(username: string, api: string) {
    console.log(username);
    return axios.get(api, {params: {username}});
}


/**
 * 创建加密密钥对，以便稍后进行无密码认证时注册公钥。
 * @param {string} username 用户名。
 * @param {string} challenge 服务器端随机生成的字符串。
 * @param {Object} [options] 可选参数。
 * @param {number} [options.timeout=60000] 用户响应生物特征/PIN检查的超时时间（毫秒）。
 * @param {'required'|'preferred'|'discouraged'} [options.userVerification='required'] 是否提示用户进行生物特征/PIN检查。
 * @param {'auto'|'local'|'roaming'|'both'}       [options.authenticatorType='auto'] 使用哪个设备作为认证器。
 * @param {boolean} [options.attestation=false] 如果启用，将提供设备证明和客户端数据作为Base64url编码的二进制数据。
 * @param {'discouraged'|'preferred'|'required'} [options.discoverable] 可发现的凭证可以使用`authenticate(...)`进行选择，而无需提供凭证ID。
 * @returns {Promise<RegistrationEncoded>} 返回注册编码信息。
 */
export async function register(username: string, challenge?: string, options?: RegisterOptions): Promise<{
    credential: {
        authenticatorAttachment: any;
        response: { clientDataJSON: string; attestationObject: string };
        rawId: string;
        id: any;
        type: any
    };
    deviceInfo: string;
    username: string
}> {
    initRandomUUID();

    options = options ?? {};
    challenge = challenge ?? window.crypto.randomUUID();

    if (!utils.isBase64url(challenge)) {
        throw new Error('challenge must be base64url');
    }

    // 创建公钥凭证创建选项
    const creationOptions: PublicKeyCredentialCreationOptions = {
        challenge: utils.parseBase64url(challenge),
        rp: {
            // id: 'mfa.greeyun.com',
            // name: 'Gree MFA Platform',
            id: window.location.hostname,
            name: window.location.hostname
        },
        user: {
            id: options.userHandle ? utils.toBuffer(options.userHandle) : await utils.sha256(new TextEncoder().encode('gree.id-user:' + username)), // 从隐私考虑，ID不应该直接可识别
            name: username,
            displayName: username
        },
        pubKeyCredParams: [
            // ES256 （Webauthn的默认算法）
            {alg: -7, type: "public-key"},
            // RS256 （适用于Windows Hello等）
            {alg: -257, type: "public-key"},
        ],
        timeout: options.timeout ?? 60000,
        authenticatorSelection: {
            // Webauthn的默认值是"preferred"
            userVerification: options.userVerification ?? "required",
            // authenticatorAttachment: await getAuthAttachment(options.authenticatorType ?? "auto"),
            authenticatorAttachment: "platform",
            // 官方默认值是'discouraged'
            residentKey: options.discoverable ?? 'preferred',
            // 主要是为了向后兼容，见https://www.w3.org/TR/webauthn/#dictionary-authenticatorSelection
            requireResidentKey: (options.discoverable === 'required')
        },
        attestation: "direct"
    }

    console.log(creationOptions);

    // 创建公钥凭证
    const credential = await navigator.credentials.create({publicKey: creationOptions}) as any //PublicKeyCredential
    console.log(credential)


    const deviceInfo = getDeviceInfo();


    // 处理凭证响应
    const response = credential.response as any // AuthenticatorAttestationResponse
    // console.log(response);


    // const posData = new FormData();
    // posData.append('username', username);
    // posData.append('credential', credential);
    //
    // console.log('posData:', posData);
    // console.log('posData to JSON:', JSON.stringify(posData));
    //
    // return await fetch(api, {
    //     method: 'POST',
    //     body: posData
    // });

    // return await axios.post(api, posData);

    return   {
        username,
        credential: {
            authenticatorAttachment: credential.authenticatorAttachment,
            id: credential.id,
            rawId: utils.toBase64url(credential.rawId),
            type: credential.type,
            response: {
                clientDataJSON: utils.toBase64url(response.clientDataJSON),
                attestationObject: utils.toBase64url(response.attestationObject)
            }
        },
        deviceInfo
    };


    // return   {
    //     username,
    //     credential: {
    //         authenticatorAttachment: credential.authenticatorAttachment,
    //         id: credential.id,
    //         rawId: utils.toBase64url(credential.rawId),
    //         type: credential.type,
    //         response: {
    //             clientData: JSON.parse(new TextDecoder('utf-8').decode(response.clientDataJSON)),
    //             attestationObject: utils.toBase64url(response.attestationObject)
    //         }
    //     },
    //     deviceInfo
    // };



    // let registration: RegistrationEncoded = {
    //     username: username,
    //     credential: {
    //         id: credential.id,
    //         publicKey: utils.toBase64url(response.getPublicKey()),
    //         algorithm: getAlgoName(credential.response.getPublicKeyAlgorithm())
    //     },
    //     authenticatorData: utils.toBase64url(response.getAuthenticatorData()),
    //     clientData: utils.toBase64url(response.clientDataJSON),
    // }
    //
    // // 如果启用了设备证明，将证明数据添加到注册信息中
    // if (options.attestation) {
    //     registration.attestationData = utils.toBase64url(response.attestationObject)
    // }

    // return registration;
}

/**
 * 根据认证类型获取支持的传输方式。
 * @param {AuthType} authType 认证类型。
 * @returns {Promise<AuthenticatorTransport[]>} 返回支持的传输方式列表。
 */
async function getTransports(authType: AuthType): Promise<AuthenticatorTransport[]> {
    const local: AuthenticatorTransport[] = ['internal']

    // 'hybrid' 在2022年中加入到规范中，目前在官方DOM类型中尚未可用
    // @ts-ignore
    const roaming: AuthenticatorTransport[] = ['hybrid', 'usb', 'ble', 'nfc']

    // 根据 authType 返回相应的传输方式列表
    if (authType === "local")
        return local
    if (authType == "roaming" || authType === "extern")
        return roaming
    if (authType === "both")
        return [...local, ...roaming]

    // 默认情况下，根据设备能力选择使用 auto 模式
    try {
        if (await isLocalAuthenticator())
            return local
        else
            return roaming
    } catch (e) {
        return [...local, ...roaming]
    }
}


/**
 * 使用提供的凭证ID之一对挑战进行签名，以验证用户身份。
 *
 * @param credentialIds
 * @param {string} challenge 服务器端随机生成的字符串，其Base64编码版本将被签名。
 * @param {Object} [options] 可选参数。
 * @param {number} [options.timeout=60000] 用户响应生物特征/PIN检查的毫秒数。
 * @param {'required'|'preferred'|'discouraged'} [options.userVerification='required'] 是否提示进行生物特征/PIN检查。
 * @param {'optional'|'conditional'|'required'|'silent'} [options.mediation='optional'] https://developer.mozilla.org/en-US/docs/Web/API/CredentialsContainer/get#mediation
 * @returns {Promise<AuthenticationEncoded>} 返回一个承诺，解决为认证编码对象。
 */
export async function authenticate(credentialIds: string[], challenge: string, options?: AuthenticateOptions):Promise<AuthenticateResponse> {


    options = options ?? {} // 如果未提供options，则默认为空对象

    // 验证挑战是否正确使用Base64url编码
    if (!utils.isBase64url(challenge))
        throw new Error('Provided challenge is not properly encoded in Base64url')

    // 获取支持的传输方式
    const transports = await getTransports(options.authenticatorType ?? "auto");

    console.log('transports: ', transports)
    console.log('credentialIds: ', credentialIds)

    // 准备公钥凭证请求选项
    let authOptions: any = {
        challenge: utils.parseBase64url(challenge), // 解码挑战
        rpId: window.location.hostname, // 请求发起者的域名
        allowCredentials: credentialIds.map(id => {
            return {
                id: utils.parseBase64url(id), // 解码凭证ID
                type: 'public-key', // 凭证类型为公钥
                transports: transports, // 支持的传输方式
            }
        }),
        pubKeyCredParams:[{ type: 'public-key', alg: -257 }],
        userVerification: options.userVerification ?? "required", // 用户验证要求
        timeout: options.timeout ?? 60000, // 超时时间
    }

    console.log('authOptions:',authOptions)

    // 如果启用了调试模式，打印authOptions
    // if (options.debug)
    //     console.debug(authOptions)

    // 请求用户凭证
    let auth = await navigator.credentials.get({
        publicKey: authOptions,
        mediation: options.mediation
    }) as PublicKeyCredential

    // 如果启用了调试模式，打印获取的凭证
    if (options.debug)
        console.debug(auth)
    console.log('auth:',auth)
    // 解析凭证响应
    const response = auth.response as AuthenticatorAssertionResponse


    const deviceInfo = getDeviceInfo();

    // const posData = {
    //     username: credentialIds[0],
    //     auth: {
    //         id: auth.id,
    //         rawId: utils.toBase64url(auth.rawId),
    //         response: {
    //             clientDataJSON: utils.toBase64url(response.clientDataJSON),
    //             authenticatorData: utils.toBase64url(response.authenticatorData),
    //             signature: utils.toBase64url(response.signature),
    //             userHandle: response.userHandle ? utils.toBase64url(response.userHandle) : undefined // 用户手柄可能不被每个验证器返回
    //         }
    //     },
    //     deviceInfo
    // }
    // console.log(posData)
    // // return await axios.post(api, posData);
    // return posData;


    // 构建认证编码对象
    return {
        id: auth.id,
        rawId: utils.toBase64url(auth.rawId),
        type: auth.type,
        response: {
            authenticatorData: utils.toBase64url(response.authenticatorData),
            clientDataJSON: utils.toBase64url(response.clientDataJSON),
            signature: utils.toBase64url(response.signature),
        },
        // userHandle: response.userHandle ? utils.toBase64url(response.userHandle) : undefined, // 用户手柄可能不被每个验证器返回
        deviceInfo
    };
}



